import { z } from 'zod';

const EnvSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_GG_API_BASE_URL: z.string().url(),
  VITE_BUILD_SHA: z.string().default('local'),
  VITE_BUILD_TIME: z.string().default(''),
  // When true, connect to local Firebase emulators (Auth + Firestore) instead
  // of the real Firebase project. Only set in test/dev — never in prod.
  // See spec 004-current-owner-uid-rename for the e2e plumbing this enables.
  VITE_USE_FIREBASE_EMULATOR: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Spec 007 feature flag — gates the "Transfer this vehicle" link on the
  // dossier and the "Accept a transfer" link in the Garage. Default false;
  // CI sets to "true" for tests; production stays false until the gg-app
  // + gg-infra companion specs deploy. See spec 007 Decision 14.
  VITE_TRANSFER_ENABLED: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Founder-facing seal-conversion insights view at /insights. Default false;
  // set to "true" in local dev or on staging to enable. Never ships to end-users
  // as a navigation item — purely a dogfood / analytics surface.
  VITE_INSIGHTS_ENABLED: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Spec 010 — Export artifact create/manage on the vehicle dossier.
  // Default false; set to "true" in staging build config once the gg-app
  // export endpoints are confirmed stable. Gated here rather than on the
  // server because the feature requires the owner to capture verifyUrl from
  // the 202 — there is no recovery path if the client doesn't store it.
  // Production stays false until the staging bake completes.
  VITE_EXPORT_ENABLED: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Public dossier page — /v/:token. Renders the redacted public provenance
  // payload (disclosure:"public"). Default false; staging sets to "true" so QR
  // codes can be tested end-to-end before the prod flip. Production stays false
  // until the gg-app GET /v1/verify/:token public endpoint is confirmed stable.
  VITE_PUBLIC_DOSSIER_ENABLED: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Photo album — vehicle photo upload + grid on the dossier.
  // Default false; staging sets to "true" once gg-app /v1/photos endpoints ship.
  // Production stays false until the staging bake completes.
  VITE_PHOTOS_ENABLED: z
    .union([z.literal('true'), z.literal('false'), z.literal('')])
    .default('false')
    .transform((v) => v === 'true'),
  // Trust layer — WebAuthn RP ID for the Absolute Pinks attestation flow.
  // Must match GG_TRUST_RP_ID on the gg-app server. "staging.absolutepinks.com"
  // is the registrable domain parent of app.staging.absolutepinks.com, so
  // WebAuthn allows its use as rpId for that subdomain (and any future ones).
  // The apex (staging.absolutepinks.com) is intentionally NXDOMAIN per
  // gg-infra spec-011; only app.staging.absolutepinks.com resolves.
  // For local dev override to: localhost
  VITE_TRUST_RP_ID: z.string().min(1).default('staging.absolutepinks.com'),
  // Comma-separated list of allowed WebAuthn origins used in assertion
  // verification (clientDataJSON.origin allowlist). Must match the origins
  // from which the PWA is served. Defaults cover staging + local dev.
  // Staging origin: https://app.staging.absolutepinks.com (spec-011 / gg-infra)
  // Example override: "https://app.staging.absolutepinks.com,http://localhost:5173"
  VITE_TRUST_ALLOWED_ORIGINS: z
    .string()
    .default(
      'https://app.staging.absolutepinks.com,http://localhost,https://localhost,http://localhost:5173,https://localhost:5173',
    ),
});

export type Env = z.infer<typeof EnvSchema>;

function parseEnv(): Env {
  const result = EnvSchema.safeParse(import.meta.env);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(
      `Invalid environment configuration. Copy .env.example → .env and fill in:\n${issues}`,
    );
  }
  return result.data;
}

export const env: Env = parseEnv();
